import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { GridApi } from 'ag-grid-enterprise'
import { EnhancedStatementRow, FinancialRowType } from '../utils/dataEnhancement'
import { FinacialStatementType } from '../types'
import { GridContext } from '../../../shared/hooks/useGridContext'
import { exportCustomReportToExcel } from '../../../shared/utils/excelJsExport/excelExport'
import { createFinancialStatementCellMetadataProvider } from '../utils/excelCellMetadataProvider'

export const useExcelExport = (
  gridApi: GridApi<EnhancedStatementRow> | undefined,
  statementType: FinacialStatementType,
  context: GridContext
) => {
  const { t } = useTranslation()
  const handleExportToExcel = useCallback(async () => {
    if (!gridApi) return

    const statementName = t(`financialStatementsPage:${statementType}`)
    const fileName = `${statementName}.xlsx`

    const allColumns = gridApi.getAllGridColumns()
    const accountCodeCol = allColumns.find(col => col.getColId() === 'accountCode')
    const nameCol = allColumns.find(col => col.getColId() === 'ag-Grid-AutoColumn')
    const dimensionCol = allColumns.find(col => col.getColId() === 'dimension')

    const otherCols = allColumns.filter(
      col =>
        col.getColId() !== 'accountCode' &&
        col.getColId() !== 'ag-Grid-AutoColumn' &&
        col.getColId() !== 'dimension' &&
        !col.getColId().startsWith('cumulative-')
    )

    const orderedColumnKeys = [
      accountCodeCol?.getColId(),
      nameCol?.getColId(),
      dimensionCol?.getColId(),
      ...otherCols.map(col => col.getColId())
    ].filter(Boolean) as string[]

    await exportCustomReportToExcel(
      [
        {
          sheetName: statementName,
          api: gridApi,
          columnKeys: orderedColumnKeys,
          shouldSkipRow: node => {
            const row = node.data as EnhancedStatementRow
            return (
              row?.rowType === FinancialRowType.DIMENSION_GROUP ||
              row?.rowType === FinancialRowType.INVESTMENT_MENU ||
              row?.rowType === FinancialRowType.AMENDMENT_MENU ||
              row?.rowType === FinancialRowType.COUNTER_AMENDMENT_MENU ||
              row?.rowType === FinancialRowType.REALLOCATION_MENU
            )
          },
          getCellMetadata: createFinancialStatementCellMetadataProvider(context)
        }
      ],
      fileName
    )
  }, [gridApi, statementType, context, t])

  return handleExportToExcel
}
